import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatErr, eur, d } from "@/lib/api";

const F = ({ id, label, value, onChange, className = "", ...p }) => (
  <div className={className}>
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <Input id={id} data-testid={`mandate-${id}-input`} value={value} onChange={onChange} className="mt-1.5 h-11 bg-white" {...p} />
  </div>
);

export const MandateForm = ({ token, info, onDone }) => {
  const prev = info.mandato?.dati || {};
  const [f, setF] = useState({ luogo_nascita: "", indirizzo: "", cap: "", citta: "", provincia: "", qualita: "titolare", de_cuius_nome: "", de_cuius_cf: "", accetta_clausole: false, ...prev });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const s = info.settings;
  const c = info.cliente;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post(`/public/mandate/${token}/generate`, f);
      onDone();
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-6" data-testid="mandate-form">
      <div className="grid sm:grid-cols-3 gap-3 text-sm bg-slate-50 rounded-lg p-4 border border-slate-200">
        <div><p className="text-xs text-slate-500">Nominativo</p><p className="font-medium text-navy">{c.nome} {c.cognome}</p></div>
        <div><p className="text-xs text-slate-500">Nato/a il</p><p className="font-medium text-navy">{d(c.data_nascita)}</p></div>
        <div><p className="text-xs text-slate-500">Codice fiscale</p><p className="font-mono text-navy">{c.codice_fiscale}</p></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <F id="luogo_nascita" label="Luogo di nascita" value={f.luogo_nascita} onChange={set("luogo_nascita")} required />
        <F id="indirizzo" label="Indirizzo di residenza" value={f.indirizzo} onChange={set("indirizzo")} required />
        <F id="citta" label="Comune" value={f.citta} onChange={set("citta")} required />
        <div className="grid grid-cols-2 gap-3">
          <F id="cap" label="CAP" value={f.cap} onChange={set("cap")} maxLength={5} required />
          <F id="provincia" label="Prov." value={f.provincia} onChange={(e) => setF({ ...f, provincia: e.target.value.toUpperCase() })} maxLength={2} required />
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Agisco in qualità di</Label>
        <RadioGroup value={f.qualita} onValueChange={(v) => setF({ ...f, qualita: v })} className="mt-2 grid sm:grid-cols-2 gap-3">
          {[["titolare", "Titolare del rapporto"], ["erede", "Erede del titolare"]].map(([v, l]) => (
            <label key={v} className={`flex items-center gap-3 border rounded-lg p-4 cursor-pointer transition-colors ${f.qualita === v ? "border-navy bg-slate-50" : "border-slate-200"}`}>
              <RadioGroupItem value={v} data-testid={`mandate-qualita-${v}`} />{l}
            </label>
          ))}
        </RadioGroup>
      </div>
      {f.qualita === "erede" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <F id="de_cuius_nome" label="Nome e cognome del defunto" value={f.de_cuius_nome} onChange={set("de_cuius_nome")} required />
          <F id="de_cuius_cf" label="C.F. del defunto" value={f.de_cuius_cf} onChange={(e) => setF({ ...f, de_cuius_cf: e.target.value.toUpperCase() })} maxLength={16} />
        </div>
      )}
      <div className="border-l-2 border-gold bg-gold-light/40 rounded-r-lg p-4 text-sm text-slate-700 space-y-1.5">
        <p className="font-semibold text-navy">Condizioni principali</p>
        <p>• Compenso: <strong>{s.success_fee_pct}%</strong> delle somme liquidate, trattenuto all'incasso. Nulla se l'esito è negativo.</p>
        <p>• Autorizzi lo studio a incassare le somme e a bonificarti il saldo entro 15 giorni lavorativi.</p>
        <p>• Penale di <strong>{eur(s.penale_bypass)}</strong> in caso di by-pass del mandato.</p>
        <p>• In caso di revoca: compenso per l'attività svolta secondo i parametri ministeriali (D.M. 55/2014).</p>
      </div>
      <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
        <Checkbox data-testid="mandate-accept-clauses-checkbox" checked={f.accetta_clausole} onCheckedChange={(v) => setF({ ...f, accetta_clausole: !!v })} className="mt-0.5" />
        <span>Ho letto e accetto le condizioni del mandato, incluse le clausole su compenso, penale, revoca e autorizzazione all'incasso.</span>
      </label>
      {err && <p data-testid="mandate-form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
      <Button data-testid="mandate-generate-btn" type="submit" disabled={loading} className="w-full sm:w-auto h-12 px-8 rounded-full bg-navy hover:bg-navy-800">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Genera il mandato precompilato"}
      </Button>
    </form>
  );
};
